/**
 * Dynamics 365 Finance & Supply Chain OAuth2 Authentication Service
 * Same pattern as BC - user connects via "Connect to D365".
 */

import { getFunctions, httpsCallable, connectFunctionsEmulator } from 'firebase/functions';
import { app } from '../firebase';

const functions = getFunctions(app, 'europe-west1');

if (process.env.NODE_ENV === 'development') {
  try {
    connectFunctionsEmulator(functions, 'localhost', 5001);
  } catch {
    // Already connected
  }
}

// D365 F&O scope: environment is set after user selects/enters it during connect flow
// We use a placeholder - the actual scope is built with environment from the connect step
const getD365Scope = (environment) =>
  environment
    ? `offline_access https://${environment}.operations.dynamics.com/.default`
    : 'offline_access https://api.businesscentral.dynamics.com/.default';

class D365Auth {
  constructor() {
    this.clientId = process.env.REACT_APP_D365_CLIENT_ID || '';
    this.redirectUri = process.env.REACT_APP_D365_REDIRECT_URI
      || (typeof window !== 'undefined' ? `${window.location.origin}/d365/callback` : 'https://app.svenry.com/d365/callback');

    this.exchangeCodeFunction = httpsCallable(functions, 'exchangeD365Code');
    this.saveTokensFunction = httpsCallable(functions, 'saveD365Tokens');
    this.getStatusFunction = httpsCallable(functions, 'getD365Status');
  }

  generateSecureState() {
    if (typeof window !== 'undefined' && window.crypto?.getRandomValues) {
      const array = new Uint32Array(4);
      window.crypto.getRandomValues(array);
      return Array.from(array, (dec) => dec.toString(36)).join('');
    }
    return Math.random().toString(36).slice(2, 15) + Math.random().toString(36).slice(2, 15) + Date.now().toString(36);
  }

  getAuthorizationUrl(state, environment) {
    if (!this.clientId) {
      throw new Error('D365 Client ID not configured (REACT_APP_D365_CLIENT_ID)');
    }
    const stateValue = state || this.generateSecureState();
    if (typeof window !== 'undefined' && window.sessionStorage) {
      sessionStorage.setItem('d365_oauth_state', stateValue);
      if (environment) sessionStorage.setItem('d365_oauth_environment', environment);
    }

    const scope = getD365Scope(environment);

    const params = new URLSearchParams({
      client_id: this.clientId,
      redirect_uri: this.redirectUri,
      response_type: 'code',
      scope,
      state: stateValue,
      response_mode: 'query',
      prompt: 'consent',
    });

    return `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?${params.toString()}`;
  }

  async exchangeCodeForToken(code, state) {
    const result = await this.exchangeCodeFunction({
      code,
      redirectUri: this.redirectUri,
      state,
    });
    return result.data;
  }

  async saveTokens(tokens, environment) {
    const result = await this.saveTokensFunction({
      accessToken: tokens.accessToken,
      refreshToken: tokens.refreshToken,
      expiresIn: tokens.expiresIn,
      expiresAt: tokens.expiresAt,
      tenantId: tokens.tenantId,
      environment: environment || sessionStorage.getItem('d365_oauth_environment') || '',
    });
    return result.data;
  }

  async getConnectionStatus() {
    const result = await this.getStatusFunction();
    return result.data;
  }

  connect(environment) {
    if (!this.clientId) {
      throw new Error('D365 Client ID not configured. Add REACT_APP_D365_CLIENT_ID to your .env file and rebuild.');
    }
    if (!environment || !environment.trim()) {
      throw new Error('D365 environment is required (e.g. your deployment hostname without .operations.dynamics.com).');
    }
    const state = this.generateSecureState();
    window.location.href = this.getAuthorizationUrl(state, environment.trim());
  }
}

const d365Auth = new D365Auth();
export default d365Auth;
